import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { useTheme } from "../context/ThemeContext";

export default function CustomerStatistics() {
  const { theme } = useTheme();
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showColumnChooser, setShowColumnChooser] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(new Set());
  const [columnChooserSearch, setColumnChooserSearch] = useState("");
  const [frozenColumns, setFrozenColumns] = useState(new Set(["slug"]));
  const [hoveredColumn, setHoveredColumn] = useState(null);

  const [sortColumn, setSortColumn] = useState("creditsConsumedSoFar");
  const [sortDirection, setSortDirection] = useState("desc");
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [columnFilters, setColumnFilters] = useState({}); // { columnName: Set of selected values }
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });
  const [editingCell, setEditingCell] = useState(null); // { rowIndex, colName }
  const [editValue, setEditValue] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);

  // Close filter dropdown on outside click or scroll
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeFilterColumn && !e.target.closest('.filter-dropdown') && !e.target.closest('.filter-button')) {
        setActiveFilterColumn(null);
      }
    };
    const handleScroll = (e) => {
      if (activeFilterColumn && !e.target.closest?.('.filter-dropdown')) {
        setActiveFilterColumn(null);
      }
    };

    if (activeFilterColumn) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [activeFilterColumn]);

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  const fetchAdminUsers = async () => {
    try {
      const response = await api.get('/api/users');
      setAdminUsers(response.data.data || []);
    } catch (err) {
      console.error('Error fetching admin users:', err);
    }
  };

  // Define column order and display names
  const columnOrder = [
    "slug",
    "accountId",
    "authAccountId",
    "creditsConsumedSoFar",
    "creditsConsumedLast24h",
    "Available Credits",
    "workflowsRanPast7days",
    "sourceCount",
    "sourceCreatedAt",
    "lastRecordCreatedAt",
    "lastRecordUpdatedAt",
    "aiFieldsCount",
    "Active in the last 7 days",
    "Expansion Opportunity",
    "Available Credits Percentage",
    "salesOwner",
    "onboardingOwner"
  ];

  // Helper to check if a column is numeric
  const isNumericColumn = (col) => {
    const normalized = normalizeColumnName(col);
    const numericCols = [
      "creditsConsumedSoFar",
      "creditsConsumedLast24h",
      "Available Credits",
      "workflowsRanPast7days",
      "sourceCount",
      "aiFieldsCount",
      "Available Credits Percentage"
    ];
    return numericCols.includes(normalized);
  };

  // Helper to check if a column is boolean
  const isBooleanColumn = (col) => {
    const normalized = normalizeColumnName(col);
    const booleanCols = [
      "Active in the last 7 days",
      "Expansion Opportunity"
    ];
    return booleanCols.includes(normalized);
  };

  const isOwnerColumn = (col) => {
    const normalized = normalizeColumnName(col);
    return normalized === "salesOwner" || normalized === "onboardingOwner";
  };

  // Map column names to normalized keys (case-insensitive matching)
  const normalizeColumnName = (colName) => {
    const lower = colName.toLowerCase().replace(/\s+/g, "");

    // Resilient matching for workflows column
    if (lower.includes("workflowsranpast7days") || lower.includes("workflowsraninthelast7days")) {
      return "workflowsRanPast7days";
    }

    const mappings = {
      "slug": "slug",
      "accountid": "accountId",
      "authaccountid": "authAccountId",
      "availablecredits": "Available Credits",
      "unusedcredits": "Available Credits",
      "creditsconsumedsofar": "creditsConsumedSoFar",
      "creditsconsumedlast24h": "creditsConsumedLast24h",
      "creditsconsumedinthelast24h": "creditsConsumedLast24h",
      "sourcecount": "sourceCount",
      "sourcescount": "sourceCount",
      "sourceupdatedat": "sourceUpdatedAt",
      "lastrecordcreatedat": "lastRecordCreatedAt",
      "lastrecordupdatedat": "lastRecordUpdatedAt",
      "aifieldscount": "aiFieldsCount",
      "sourcecreatedat": "sourceCreatedAt",
      "isactive": "Active in the last 7 days",
      "activelast7days": "Active in the last 7 days",
      "activeinthelast7days": "Active in the last 7 days",
      "expansionopportunity": "Expansion Opportunity",
      "availablecreditspercentage": "Available Credits Percentage",
      "availablecreditspct": "Available Credits Percentage",
      "salesowner": "salesOwner",
      "onboardingowner": "onboardingOwner",
      "availablecreditspercentagepct": "Available Credits Percentage",
      "avaialblecreditspercentage": "Available Credits Percentage" // Handling typo
    };
    return mappings[lower] || colName;
  };

  // Find matching column from actual keys
  const findMatchingColumn = (targetCol, actualKeys) => {
    const targetLower = targetCol.toLowerCase().replace(/\s+/g, "");

    // Direct match
    const directMatch = actualKeys.find(key =>
      key.toLowerCase().replace(/\s+/g, "") === targetLower
    );
    if (directMatch) return directMatch;

    // Partial match for variations
    const partialMatches = {
      "slug": actualKeys.find(k => k.toLowerCase().includes("slug")),
      "accountid": actualKeys.find(k => k.toLowerCase() === "accountid"),
      "authaccountid": actualKeys.find(k => k.toLowerCase() === "authaccountid"),
      "availablecredits": actualKeys.find(k => (k.toLowerCase().includes("unused") && k.toLowerCase().includes("credit")) || (k.toLowerCase().includes("available") && k.toLowerCase().includes("credit"))),
      "creditsconsumedsofar": actualKeys.find(k => k.toLowerCase().includes("creditsconsumedsofar") || k.toLowerCase().includes("creditsconsumedsofar")),
      "creditsconsumedlast24h": actualKeys.find(k => k.toLowerCase().includes("creditsconsumedlast24h") || k.toLowerCase().includes("creditsconsumedinthelast24h")),
      "workflowsranpast7days": actualKeys.find(k => k.toLowerCase().includes("workflowsranpast7days") || k.toLowerCase().includes("workflowsraninthelast7days")),
      "sourcecount": actualKeys.find(k => k.toLowerCase().includes("sourcecount") || (k.toLowerCase().includes("source") && k.toLowerCase().includes("count"))),
      "sourceupdatedat": actualKeys.find(k => k.toLowerCase().includes("sourceupdatedat") || (k.toLowerCase().includes("source") && k.toLowerCase().includes("updated"))),
      "lastrecordcreatedat": actualKeys.find(k => k.toLowerCase() === "recordtypecreated" || (k.toLowerCase().includes("record") && k.toLowerCase().includes("created"))),
      "lastrecordupdatedat": actualKeys.find(k => k.toLowerCase() === "lasttimerecordupdated" || k.toLowerCase() === "lastrec" || (k.toLowerCase().includes("record") && k.toLowerCase().includes("updated") && !k.toLowerCase().includes("type"))),
      "aifieldscount": actualKeys.find(k => k.toLowerCase().includes("ai") && k.toLowerCase().includes("fields") && k.toLowerCase().includes("count")),
      "sourcecreatedat": actualKeys.find(k => k.toLowerCase().includes("sourcecreatedat") || (k.toLowerCase().includes("source") && k.toLowerCase().includes("created"))),
      "activelast7days": actualKeys.find(k => k.toLowerCase().includes("isactive")),
      "availablecreditspct": actualKeys.find(k => k.toLowerCase().includes("availablecreditspct")),
      "expansionopportunity": actualKeys.find(k => k.toLowerCase().includes("expansionopportunity")),
      "salesowner": actualKeys.find(k => k.toLowerCase() === "salesowner"),
      "onboardingowner": actualKeys.find(k => k.toLowerCase() === "onboardingowner"),
    };

    return partialMatches[targetLower] || null;
  };

  // Fetch data when date changes
  useEffect(() => {
    if (!date) return;

    setLoading(true);
    api.get(`/api/customer-statistics?date=${date}`)
      .then((res) => {
        const rowData = Array.isArray(res.data.data) ? res.data.data : [];
        setRows(rowData);

        // Find the actual key for the default sort column
        if (rowData.length > 0) {
          const actualKeys = Object.keys(rowData[0]);
          const defaultSortKey = findMatchingColumn("creditsConsumedSoFar", actualKeys);
          if (defaultSortKey) {
            setSortColumn(defaultSortKey);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load customer statistics", err);
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [date]);

  // Order columns according to specified order
  const allColumns = useMemo(() => {
    if (!rows.length) return [];

    const allKeys = Object.keys(rows[0]);
    const orderedColumns = [];
    const usedKeys = new Set();

    // Add columns in specified order by finding matches
    columnOrder.forEach(targetCol => {
      const matched = findMatchingColumn(targetCol, allKeys);
      if (matched && !usedKeys.has(matched)) {
        orderedColumns.push(matched);
        usedKeys.add(matched);
      }
    });

    // Add remaining columns (AI columns first, then others)
    const aiColumns = [];
    const otherColumns = [];

    allKeys.forEach(key => {
      const isSegmentNames = key.toLowerCase() === "segmentnames";
      if (!usedKeys.has(key) && !isSegmentNames) {
        const lowerKey = key.toLowerCase();
        if (lowerKey.startsWith('ai') || lowerKey.includes('ai')) {
          aiColumns.push(key);
        } else {
          otherColumns.push(key);
        }
      }
    });

    return [...orderedColumns, ...aiColumns, ...otherColumns];
  }, [rows]);

  // Initialize visible columns when data loads
  useEffect(() => {
    if (allColumns.length > 0 && visibleColumns.size === 0) {
      // By default, show all columns except IDs (user can customize)
      const defaultVisible = new Set(allColumns);
      setVisibleColumns(defaultVisible);
    }
  }, [allColumns]);

  // Filter columns based on visibility
  const columns = useMemo(() => {
    if (visibleColumns.size === 0) return allColumns;
    return allColumns.filter(col => visibleColumns.has(col));
  }, [allColumns, visibleColumns]);

  // Extract unique values for owner columns for filtering
  const uniqueOwnerValues = useMemo(() => {
    const ownerCounts = {}; // { col: { value: count } }
    ["salesOwner", "onboardingOwner"].forEach(col => {
      ownerCounts[col] = {};
      rows.forEach(row => {
        const val = row[col] || "Unassigned";
        ownerCounts[col][val] = (ownerCounts[col][val] || 0) + 1;
      });
    });
    return ownerCounts;
  }, [rows]);

  // Filter rows based on search query and column filters
  const filteredRows = useMemo(() => {
    let result = rows;

    // Apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(row => {
        return Object.values(row).some(value => {
          if (value == null) return false;
          return String(value).toLowerCase().includes(query);
        });
      });
    }

    // Apply column filters
    Object.entries(columnFilters).forEach(([col, filter]) => {
      if (filter && ((filter.value !== "" && filter.value != null) || (filter.selectedValues && filter.selectedValues.size > 0))) {
        const isNumeric = isNumericColumn(col);
        const isBoolean = isBooleanColumn(col);

        if (isNumeric) {
          let filterVal = Number(filter.value);
          // If it's a percentage column, divide by 100 since data is 0.xx
          // We normalize before comparing to handle name variations
          if (normalizeColumnName(col) === "Available Credits Percentage") {
            filterVal = filterVal / 100;
          }

          result = result.filter(row => {
            const rowVal = Number(row[col] ?? 0);
            switch (filter.operator) {
              case ">": return rowVal > filterVal;
              case "<": return rowVal < filterVal;
              case ">=": return rowVal >= filterVal;
              case "<=": return rowVal <= filterVal;
              case "=": return rowVal === filterVal;
              case "!=": return rowVal !== filterVal;
              default: return true;
            }
          });
        } else if (isBoolean) {
          result = result.filter(row => {
            const rowVal = row[col];
            if (filter.value === "true") return rowVal === true;
            if (filter.value === "false") return rowVal === false;
            if (filter.value === "empty") return rowVal == null || rowVal === "";
            return true;
          });
        } else if (isOwnerColumn(col) && filter.selectedValues && filter.selectedValues.size > 0) {
          result = result.filter(row => {
            const val = row[col] || "Unassigned";
            return filter.selectedValues.has(val);
          });
        }
      }
    });

    return result;
  }, [rows, searchQuery, columnFilters]);

  // Sorting logic (handles numbers, strings, nulls, spaces in keys)
  const sortedRows = useMemo(() => {
    if (!sortColumn) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      const av = a[sortColumn];
      const bv = b[sortColumn];

      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      if (!isNaN(av) && !isNaN(bv)) {
        return sortDirection === "asc"
          ? Number(av) - Number(bv)
          : Number(bv) - Number(av);
      }

      return sortDirection === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [filteredRows, sortColumn, sortDirection]);

  // Export to CSV
  const handleExportToCSV = () => {
    if (sortedRows.length === 0) {
      alert("No data to export");
      return;
    }

    // Get headers from visible columns
    const headers = columns;

    // Create CSV content
    const csvRows = [];

    // Add header row with formatted names
    csvRows.push(headers.map(col => formatColumnName(col)).join(","));

    // Add data rows
    sortedRows.forEach(row => {
      const values = headers.map(col => {
        const value = row[col];
        if (value == null || value === "") return "";
        // Escape commas and quotes in CSV
        const stringValue = String(value).replace(/"/g, '""');
        return `"${stringValue}"`;
      });
      csvRows.push(values.join(","));
    });

    // Create blob and download
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `customer-statistics-${date || "export"}-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Toggle column visibility
  const toggleColumn = (col) => {
    const newVisible = new Set(visibleColumns);
    if (newVisible.has(col)) {
      newVisible.delete(col);
    } else {
      newVisible.add(col);
    }
    setVisibleColumns(newVisible);
  };

  // Handle numeric filter changes
  const handleNumericFilter = (col, operator, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [col]: { operator, value }
    }));
  };

  // Handle owner (string) filter selection
  const handleOwnerFilter = (col, value) => {
    setColumnFilters(prev => {
      const current = prev[col]?.selectedValues || new Set();
      const next = new Set(current);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return {
        ...prev,
        [col]: { selectedValues: next }
      };
    });
  };

  const handleSaveCellValue = async (row, colName, newValue) => {
    if (!row.accountId) return;
    setIsSavingEdit(true);
    try {
      const normalizedCol = normalizeColumnName(colName);
      const payload = {};
      if (normalizedCol === "salesOwner") payload.salesOwner = newValue;
      if (normalizedCol === "onboardingOwner") payload.onboardingOwner = newValue;

      await api.post(`/api/workspace/${row.accountId}/save-domain`, payload);

      // Update local state
      setRows(prevRows => prevRows.map(r => {
        if (r.accountId === row.accountId) {
          return { ...r, [colName]: newValue };
        }
        return r;
      }));
      setEditingCell(null);
    } catch (err) {
      console.error("Failed to save owner update", err);
      alert("Failed to save update. Please try again.");
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Clear all filters for a column
  const clearColumnFilter = (col) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[col];
      return newFilters;
    });
  };

  // Toggle column freeze
  const toggleFreezeColumn = (col) => {
    const isSlug = col.toLowerCase() === "slug";
    if (isSlug) return; // Slug cannot be unfrozen

    const newFrozen = new Set(frozenColumns);
    if (newFrozen.has(col)) {
      newFrozen.delete(col);
    } else {
      newFrozen.add(col);
    }
    setFrozenColumns(newFrozen);
  };

  // Get column width
  const getColumnWidth = (col) => {
    const colLower = col.toLowerCase();
    if (colLower === "slug") return 150;
    // Wider columns for IDs and long content
    if (colLower.includes("accountid") || colLower.includes("authaccountid")) return 200;
    // Long column names need more space
    if (colLower.includes("creditsconsumedsofar") || colLower.includes("creditsconsumedlast24h")) return 180;
    if (colLower.includes("workflowsranpast") || colLower.includes("workflowsraninthelast")) return 180;
    if (colLower.includes("lastrecordcreated") || colLower.includes("recordtypecreated")) return 200;
    if (colLower.includes("lastrecordupdated") || colLower.includes("lasttimerecordupdated")) return 200;
    if (colLower.includes("credits") || colLower.includes("workflow")) return 160;
    if (colLower.includes("source")) return 150;
    if (colLower.includes("ai") && colLower.includes("count")) return 150;
    return 140; // Default width increased
  };


  // Calculate left offset for frozen columns - stacks them from left to right
  const getFrozenLeftOffset = (col, colIndex) => {
    if (!frozenColumns.has(col)) return null;

    let offset = 0;
    // Calculate cumulative offset based on all frozen columns before this one in the column order
    for (let i = 0; i < colIndex; i++) {
      if (frozenColumns.has(columns[i])) {
        offset += getColumnWidth(columns[i]);
      }
    }
    return offset;
  };

  // Format column name for display
  const formatColumnName = (colName) => {
    const normalized = normalizeColumnName(colName);

    // Special cases
    const specialCases = {
      "Available Credits Percentage": "Available Credits %",
    };
    if (specialCases[normalized]) return specialCases[normalized];

    // Handle camelCase
    return normalized
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Format cell value for display
  const formatCellValue = (value) => {
    if (value == null || value === "") return "-";
    if (typeof value === "number") {
      return value.toLocaleString();
    }
    return value;
  };

  return (
    <div style={{
      padding: "24px",
      backgroundColor: "#f8f9fa",
      minHeight: "100vh",
      fontFamily: "Arial, sans-serif",
      overflowX: "hidden", // Prevent horizontal scroll on main container
      overflowY: "auto",
      width: "100%",
      boxSizing: "border-box",
      position: "relative"
    }}>
      {/* Header Section */}
      <div style={{
        marginBottom: "24px",
        paddingBottom: "16px",
        borderBottom: "2px solid #e0e0e0",
        width: "100%",
        maxWidth: "100%",
        position: "relative",
        boxSizing: "border-box"
      }}>
        <div style={{
          marginBottom: "16px",
          textAlign: "center",
          width: "100%"
        }}>
          <h1 style={{
            margin: 0,
            fontSize: "28px",
            fontWeight: "600",
            color: "#2c3e50",
            marginBottom: "8px"
          }}>
            Customer Statistics
          </h1>
          <p style={{
            margin: 0,
            fontSize: "14px",
            color: "#7f8c8d"
          }}>
            View and analyze customer usage statistics by date
          </p>
        </div>
        {/* Search Input - Centered */}
        {rows.length > 0 && (
          <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            position: "relative",
            left: 0,
            right: 0
          }}>
            <input
              type="text"
              placeholder="Search across all columns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
                outline: "none",
                transition: "border-color 0.3s",
                width: "400px",
                maxWidth: "90%",
                boxSizing: "border-box"
              }}
              onFocus={(e) => e.target.style.borderColor = "#3498db"}
              onBlur={(e) => e.target.style.borderColor = "#ddd"}
            />
          </div>
        )}
      </div>

      {/* Controls Section */}
      <div style={{
        backgroundColor: "white",
        padding: "20px",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        marginBottom: "24px",
        display: "flex",
        gap: "16px",
        alignItems: "center",
        flexWrap: "wrap",
        maxWidth: "100%",
        overflowX: "hidden" // Prevent horizontal scroll
      }}>
        {/* Refresh Warning */}
        <div style={{
          width: "100%",
          padding: "8px 12px",
          backgroundColor: "#fff3cd",
          color: "#856404",
          border: "1px solid #ffeeba",
          borderRadius: "4px",
          fontSize: "13px",
          marginBottom: "8px",
          display: "flex",
          alignItems: "center",
          gap: "8px"
        }}>
          <span>⚠️ Data gets refreshed every 24 hours</span>
        </div>

        {/* Date Picker and Sort Controls */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <label style={{
              fontSize: "14px",
              fontWeight: "500",
              color: "#34495e"
            }}>
              Select Date:
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
                outline: "none",
                transition: "border-color 0.3s",
                cursor: "pointer"
              }}
              onFocus={(e) => e.target.style.borderColor = "#3498db"}
              onBlur={(e) => e.target.style.borderColor = "#ddd"}
            />
          </div>

          {/* Sorting Controls - Close to Date */}
          {columns.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <label style={{
                fontSize: "14px",
                fontWeight: "500",
                color: "#34495e"
              }}>
                Sort by:
              </label>
              <select
                value={sortColumn}
                onChange={(e) => setSortColumn(e.target.value)}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                  outline: "none",
                  cursor: "pointer",
                  backgroundColor: "white",
                  minWidth: "180px"
                }}
              >
                <option value="">Select column...</option>
                {columns.map((col) => (
                  <option key={col} value={col}>
                    {formatColumnName(col)}
                  </option>
                ))}
              </select>

              <select
                value={sortDirection}
                onChange={(e) => setSortDirection(e.target.value)}
                disabled={!sortColumn}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                  outline: "none",
                  cursor: sortColumn ? "pointer" : "not-allowed",
                  backgroundColor: sortColumn ? "white" : "#f5f5f5",
                  opacity: sortColumn ? 1 : 0.6,
                  minWidth: "70px"
                }}
              >
                <option value="asc">Asc</option>
                <option value="desc">Desc</option>
              </select>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {rows.length > 0 && (
          <>
            <div style={{
              height: "24px",
              width: "1px",
              backgroundColor: "#ddd"
            }} />
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                onClick={() => setShowColumnChooser(!showColumnChooser)}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500"
                }}
              >
                📋 Manage columns
              </button>
              <button
                onClick={handleExportToCSV}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                📥 Export CSV
              </button>
            </div>
          </>
        )}
      </div>

      {/* Column Chooser Modal - Single Panel Design */}
      {showColumnChooser && allColumns.length > 0 && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }} onClick={() => setShowColumnChooser(false)}>
          <div style={{
            backgroundColor: theme.cardBackground,
            borderRadius: "8px",
            padding: "24px",
            maxWidth: "500px",
            width: "90%",
            maxHeight: "80vh",
            boxShadow: `0 4px 12px ${theme.cardShadow}`,
            border: `1px solid ${theme.border}`
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px"
            }}>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600", color: theme.text }}>
                Manage columns
              </h3>
              <button
                onClick={() => setShowColumnChooser(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: theme.textMuted
                }}
              >
                ×
              </button>
            </div>

            {/* Single Search Bar at Top */}
            <div style={{ marginBottom: "20px" }}>
              <input
                type="text"
                placeholder="Search..."
                value={columnChooserSearch}
                onChange={(e) => setColumnChooserSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: "4px",
                  fontSize: "14px",
                  backgroundColor: theme.inputBackground,
                  color: theme.text
                }}
              />
            </div>

            {/* Single Panel with All Columns */}
            <div style={{
              border: `1px solid ${theme.border}`,
              borderRadius: "4px",
              padding: "16px",
              marginBottom: "20px",
              maxHeight: "400px",
              overflowY: "auto"
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "12px"
              }}>
                <input
                  type="checkbox"
                  checked={allColumns.filter(col => {
                    const isSlug = col.toLowerCase() === "slug";
                    if (isSlug) return true; // Slug is always checked
                    const matches = !columnChooserSearch || formatColumnName(col).toLowerCase().includes(columnChooserSearch.toLowerCase());
                    return matches && visibleColumns.has(col);
                  }).length === allColumns.filter(col => {
                    const isSlug = col.toLowerCase() === "slug";
                    if (isSlug) return true;
                    return !columnChooserSearch || formatColumnName(col).toLowerCase().includes(columnChooserSearch.toLowerCase());
                  }).length}
                  onChange={(e) => {
                    const filteredCols = allColumns.filter(col => {
                      const isSlug = col.toLowerCase() === "slug";
                      if (isSlug) return false; // Don't include slug in toggle
                      return !columnChooserSearch || formatColumnName(col).toLowerCase().includes(columnChooserSearch.toLowerCase());
                    });
                    const newVisible = new Set(visibleColumns);
                    if (e.target.checked) {
                      filteredCols.forEach(col => newVisible.add(col));
                    } else {
                      filteredCols.forEach(col => {
                        if (col.toLowerCase() !== "slug") {
                          newVisible.delete(col);
                        }
                      });
                    }
                    setVisibleColumns(newVisible);
                  }}
                  style={{ cursor: "pointer" }}
                />
                <span style={{ fontSize: "14px", fontWeight: "500" }}>
                  {allColumns.filter(col => {
                    const isSlug = col.toLowerCase() === "slug";
                    if (isSlug) return true;
                    return !columnChooserSearch || formatColumnName(col).toLowerCase().includes(columnChooserSearch.toLowerCase());
                  }).length} items
                </span>
              </div>
              <div style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px"
              }}>
                {allColumns
                  .filter(col => {
                    if (!columnChooserSearch) return true;
                    return formatColumnName(col).toLowerCase().includes(columnChooserSearch.toLowerCase());
                  })
                  .map((col) => {
                    const isSlug = col.toLowerCase() === "slug";
                    const isVisible = visibleColumns.has(col);
                    return (
                      <label
                        key={col}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: isSlug ? "not-allowed" : "pointer",
                          padding: "8px",
                          borderRadius: "4px",
                          backgroundColor: isSlug ? "#e8f4f8" : "transparent"
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isVisible}
                          onChange={() => {
                            if (isSlug) return; // Prevent hiding slug
                            toggleColumn(col);
                          }}
                          disabled={isSlug}
                          style={{ cursor: isSlug ? "not-allowed" : "pointer" }}
                        />
                        <span style={{
                          fontSize: "14px",
                          color: isSlug ? "#007bff" : "#2c3e50",
                          fontWeight: isSlug ? "600" : "normal"
                        }}>
                          {formatColumnName(col)}
                          {isSlug && " (Always visible)"}
                        </span>
                      </label>
                    );
                  })}
              </div>
            </div>

            {/* Bottom Actions */}
            <div style={{
              display: "flex",
              gap: "10px",
              justifyContent: "flex-end",
              borderTop: "1px solid #e0e0e0",
              paddingTop: "16px"
            }}>
              <button
                onClick={() => setShowColumnChooser(false)}
                style={{
                  padding: "8px 20px",
                  backgroundColor: "white",
                  color: "#28a745",
                  border: "1px solid #28a745",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500"
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => setShowColumnChooser(false)}
                style={{
                  padding: "8px 20px",
                  backgroundColor: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500"
                }}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{
          textAlign: "center",
          padding: "40px",
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
        }}>
          <div style={{
            display: "inline-block",
            width: "40px",
            height: "40px",
            border: "4px solid #e3e3e3",
            borderTop: "4px solid #3498db",
            borderRadius: "50%",
            animation: "spin 1s linear infinite"
          }} />
          <p style={{ marginTop: "16px", color: "#7f8c8d" }}>Loading statistics...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && date && rows.length === 0 && (
        <div style={{
          textAlign: "center",
          padding: "40px",
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
        }}>
          <p style={{ fontSize: "16px", color: "#7f8c8d" }}>
            No data available for the selected date
          </p>
        </div>
      )}

      {/* Table */}
      {!loading && rows.length > 0 && (
        <div style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          overflow: "visible",
          maxWidth: "100%",
          width: "100%",
          position: "relative"
        }}>
          <div style={{
            overflowX: "auto",
            overflowY: "visible",
            position: "relative",
            width: "100%",
            WebkitOverflowScrolling: "touch",
            border: "1px solid #e0e0e0",
            borderRadius: "8px"
          }}>
            <table style={{
              width: "100%",
              tableLayout: "fixed",
              borderCollapse: "collapse",
              fontSize: "14px",
              minWidth: columns.length > 0 ? `${columns.reduce((sum, col) => sum + getColumnWidth(col), 0)}px` : "100%"
            }}>
              <thead style={{ overflow: "visible" }}>
                <tr style={{ backgroundColor: "#34495e" }}>
                  {columns.map((col, colIndex) => {
                    const isSlug = col.toLowerCase() === "slug";
                    const isFrozen = frozenColumns.has(col);
                    const leftOffset = getFrozenLeftOffset(col, colIndex);
                    const isHovered = hoveredColumn === col;
                    const canFreeze = !isSlug; // Slug is always frozen
                    const colWidth = getColumnWidth(col);

                    return (
                      <th
                        key={col}
                        onMouseEnter={() => setHoveredColumn(col)}
                        onMouseLeave={() => setHoveredColumn(null)}
                        onClick={() => canFreeze && toggleFreezeColumn(col)}
                        style={{
                          textAlign: "left",
                          padding: "10px 12px",
                          color: "white",
                          fontWeight: "600",
                          whiteSpace: "normal",
                          borderRight: "1px solid rgba(255,255,255,0.1)",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.3px",
                          width: `${colWidth}px`,
                          minWidth: `${colWidth}px`,
                          maxWidth: `${colWidth}px`,
                          position: isFrozen ? "sticky" : "relative",
                          left: isFrozen && leftOffset !== null ? `${leftOffset}px` : "auto",
                          zIndex: isFrozen ? 10 + colIndex : 1,
                          backgroundColor: isHovered && canFreeze ? "#2c3e50" : "#34495e",
                          boxShadow: isFrozen ? "2px 0 4px rgba(0,0,0,0.1)" : "none",
                          cursor: canFreeze ? "pointer" : "default",
                          transition: "background-color 0.2s",
                          overflow: "visible",
                          wordBreak: "break-word",
                          lineHeight: "1.4"
                        }}
                        title={canFreeze ? (isFrozen ? "Click to unfreeze column" : "Click to freeze column") : "Always frozen"}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "4px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }} onClick={() => canFreeze && toggleFreezeColumn(col)}>
                            {formatColumnName(col)}
                            {isFrozen && <span style={{ fontSize: "10px", opacity: 0.7 }}>🔒</span>}
                          </div>
                          {(isNumericColumn(col) || isBooleanColumn(col) || isOwnerColumn(col)) && (
                            <button
                              className="filter-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (activeFilterColumn === col) {
                                  setActiveFilterColumn(null);
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setFilterPosition({
                                    top: rect.bottom,
                                    left: rect.left
                                  });
                                  setActiveFilterColumn(col);
                                  setFilterSearch("");
                                }
                              }}
                              style={{
                                background: (columnFilters[col] && (columnFilters[col].value !== "" || (columnFilters[col].selectedValues && columnFilters[col].selectedValues.size > 0))) ? "#3498db" : "transparent",
                                border: "none",
                                color: "white",
                                cursor: "pointer",
                                padding: "2px 4px",
                                borderRadius: "3px",
                                fontSize: "10px",
                                opacity: (columnFilters[col] && (columnFilters[col].value !== "" || (columnFilters[col].selectedValues && columnFilters[col].selectedValues.size > 0))) ? 1 : 0.6
                              }}
                              title="Filter this column"
                            >
                              ▼
                            </button>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {sortedRows.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    style={{
                      backgroundColor: rowIndex % 2 === 0 ? "white" : "#f8f9fa",
                      transition: "background-color 0.2s",
                      borderBottom: "1px solid #e0e0e0"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#e8f4f8";
                      // Update all cells in the row including frozen columns
                      Array.from(e.currentTarget.querySelectorAll('td')).forEach(td => {
                        const isFrozen = td.getAttribute('data-frozen') === 'true';
                        if (isFrozen) {
                          td.style.backgroundColor = "#e8f4f8";
                        }
                      });
                    }}
                    onMouseLeave={(e) => {
                      const baseColor = rowIndex % 2 === 0 ? "white" : "#f8f9fa";
                      e.currentTarget.style.backgroundColor = baseColor;
                      // Update all cells in the row including frozen columns
                      Array.from(e.currentTarget.querySelectorAll('td')).forEach(td => {
                        const isFrozen = td.getAttribute('data-frozen') === 'true';
                        if (isFrozen) {
                          td.style.backgroundColor = baseColor;
                        }
                      });
                    }}
                  >
                    {columns.map((col, colIndex) => {
                      const isSlug = col.toLowerCase() === "slug";
                      const isFrozen = frozenColumns.has(col);
                      const cellValue = formatCellValue(row[col]);
                      const baseBgColor = rowIndex % 2 === 0 ? "white" : "#f8f9fa";
                      const leftOffset = getFrozenLeftOffset(col, colIndex);
                      const colWidth = getColumnWidth(col);

                      return (
                        <td
                          key={col}
                          data-slug={isSlug}
                          data-frozen={isFrozen}
                          style={{
                            padding: "10px 12px",
                            whiteSpace: "normal",
                            color: "#2c3e50",
                            borderRight: "1px solid #e0e0e0",
                            fontSize: "13px",
                            width: `${colWidth}px`,
                            minWidth: `${colWidth}px`,
                            maxWidth: `${colWidth}px`,
                            overflow: "visible",
                            wordBreak: "break-word",
                            lineHeight: "1.4",
                            position: isFrozen ? "sticky" : "relative",
                            left: isFrozen && leftOffset !== null ? `${leftOffset}px` : "auto",
                            zIndex: isFrozen ? 20 + colIndex : 1,
                            backgroundColor: isFrozen ? baseBgColor : "white",
                            boxShadow: isFrozen ? "2px 0 5px rgba(0,0,0,0.15)" : "none",
                            transition: "background-color 0.2s"
                          }}
                        >
                          {(() => {
                            const normalizedCol = normalizeColumnName(col);
                            const segmentKey = Object.keys(row).find(k => k.toLowerCase() === "segmentnames");
                            const segments = segmentKey ? row[segmentKey] : null;

                            // Format percentage for Available Credits Percentage
                            if (normalizedCol === "Available Credits Percentage") {
                              const numVal = parseFloat(cellValue);
                              if (!isNaN(numVal)) {
                                return `${(numVal * 100).toFixed(2)}%`;
                              }
                              return cellValue;
                            }

                            // Make slug clickable - redirect to workspace-info
                            if (normalizedCol === "slug" && cellValue) {
                              return (
                                <a
                                  href={`/workspace-info?slug=${encodeURIComponent(cellValue)}`}
                                  style={{
                                    color: "#3498db",
                                    textDecoration: "underline",
                                    cursor: "pointer"
                                  }}
                                >
                                  {cellValue}
                                </a>
                              );
                            }

                            // Format boolean columns as Yes/No
                            if (normalizedCol === "Expansion Opportunity" || normalizedCol === "Active in the last 7 days") {
                              if (cellValue === true || cellValue === "true") {
                                return "Yes";
                              } else if (cellValue === false || cellValue === "false") {
                                return "No";
                              }
                              return cellValue ?? "-";
                            }

                            if (normalizedCol === "workflowsRanPast7days" && segments && segments.length > 0) {
                              return (
                                <button
                                  onClick={() => {
                                    const arraySegments = Array.isArray(segments)
                                      ? segments
                                      : (typeof segments === 'string' ? segments.split(',').map(s => s.trim()) : [segments]);
                                    setSelectedSegments(arraySegments);
                                    setShowSegmentModal(true);
                                  }}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    color: "#3498db",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    padding: 0,
                                    fontSize: "inherit",
                                    textAlign: "left"
                                  }}
                                >
                                  {cellValue}
                                </button>
                              );
                            }

                            // Editable owner columns
                            if (isOwnerColumn(col)) {
                              const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.colName === col;
                              if (isEditing) {
                                return (
                                  <select
                                    autoFocus
                                    value={editValue}
                                    onChange={(e) => {
                                      setEditValue(e.target.value);
                                      handleSaveCellValue(row, col, e.target.value);
                                    }}
                                    onBlur={() => setEditingCell(null)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Escape") setEditingCell(null);
                                    }}
                                    disabled={isSavingEdit}
                                    style={{
                                      width: "100%",
                                      padding: "4px 6px",
                                      fontSize: "13px",
                                      borderRadius: "4px",
                                      border: "1px solid #3498db"
                                    }}
                                  >
                                    <option value="">Unassigned</option>
                                    {adminUsers.map(u => (
                                      <option key={u.email} value={u.name}>{u.name}</option>
                                    ))}
                                  </select>
                                );
                              }
                              return (
                                <div
                                  onDoubleClick={() => {
                                    setEditingCell({ rowIndex, colName: col });
                                    setEditValue(row[col] || "");
                                  }}
                                  style={{
                                    cursor: "pointer",
                                    padding: "4px 0",
                                    minHeight: "20px",
                                    color: !row[col] ? "#95a5a6" : "inherit",
                                    borderBottom: "1px dashed transparent",
                                    transition: "border-bottom 0.2s",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    justifyContent: "space-between"
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.borderBottomColor = "#ddd";
                                    const icon = e.currentTarget.querySelector('.edit-icon');
                                    if (icon) icon.style.opacity = '1';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.borderBottomColor = "transparent";
                                    const icon = e.currentTarget.querySelector('.edit-icon');
                                    if (icon) icon.style.opacity = '0.7';
                                  }}
                                  title="Click icon or double-click to edit"
                                >
                                  <span style={{ flex: 1 }}>{cellValue || "Unassigned"}</span>
                                  <span
                                    className="edit-icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingCell({ rowIndex, colName: col });
                                      setEditValue(row[col] || "");
                                    }}
                                    style={{
                                      fontSize: "12px",
                                      opacity: 0.7,
                                      transition: "all 0.2s",
                                      padding: "3px 6px",
                                      borderRadius: "4px",
                                      backgroundColor: "#e3f2fd",
                                      color: "#1976d2",
                                      border: "1px solid #bbdefb",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center"
                                    }}
                                  >
                                    ✎
                                  </span>
                                </div>
                              );
                            }
                            return cellValue;
                          })()}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer with row count */}
          <div style={{
            padding: "12px 16px",
            backgroundColor: "#f8f9fa",
            borderTop: "1px solid #e0e0e0",
            fontSize: "13px",
            color: "#7f8c8d",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <div>
              {searchQuery && (
                <span>
                  Filtered: {sortedRows.length} of {rows.length} {rows.length === 1 ? "row" : "rows"}
                </span>
              )}
              {!searchQuery && (
                <span>
                  Showing {sortedRows.length} {sortedRows.length === 1 ? "row" : "rows"}
                </span>
              )}
            </div>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                style={{
                  padding: "4px 12px",
                  backgroundColor: "transparent",
                  color: "#7f8c8d",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px"
                }}
              >
                Clear search
              </button>
            )}
          </div>
        </div >
      )
      }

      {/* Segment Names Modal */}
      {
        showSegmentModal && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000
          }} onClick={() => setShowSegmentModal(false)}>
            <div style={{
              backgroundColor: theme.cardBackground,
              borderRadius: "8px",
              padding: "24px",
              maxWidth: "500px",
              width: "90%",
              maxHeight: "80vh",
              boxShadow: `0 4px 12px ${theme.cardShadow}`,
              display: "flex",
              flexDirection: "column",
              border: `1px solid ${theme.border}`
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px"
              }}>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600", color: theme.text }}>
                  Segment Names
                </h3>
                <button
                  onClick={() => setShowSegmentModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "24px",
                    cursor: "pointer",
                    color: theme.textMuted
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{
                overflowY: "auto",
                paddingRight: "10px"
              }}>
                <ul style={{
                  margin: 0,
                  padding: "0 0 0 20px",
                  color: theme.text
                }}>
                  {Array.isArray(selectedSegments) && selectedSegments.map((name, idx) => (
                    <li key={idx} style={{ marginBottom: "8px", fontSize: "14px" }}>
                      {name}
                    </li>
                  ))}
                </ul>
                {(!selectedSegments || (Array.isArray(selectedSegments) && selectedSegments.length === 0)) && (
                  <p style={{ color: theme.textMuted, fontSize: "14px" }}>No segments found.</p>
                )}
              </div>

              <div style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "flex-end"
              }}>
                <button
                  onClick={() => setShowSegmentModal(false)}
                  style={{
                    padding: "8px 20px",
                    backgroundColor: theme.primary,
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500"
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Fixed Filter Dropdown */}
      {activeFilterColumn && (
        <div
          className="filter-dropdown"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: `${filterPosition.top}px`,
            left: `${filterPosition.left}px`,
            marginTop: "2px",
            backgroundColor: theme.cardBackground,
            border: `1px solid ${theme.border}`,
            borderRadius: "4px",
            boxShadow: `0 4px 12px ${theme.cardShadow}`,
            zIndex: 9999,
            minWidth: "220px",
            maxHeight: "350px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
            <div style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>Filter Rule</div>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {isNumericColumn(activeFilterColumn) ? (
                <>
                  <select
                    value={columnFilters[activeFilterColumn]?.operator || ">"}
                    onChange={(e) => handleNumericFilter(activeFilterColumn, e.target.value, columnFilters[activeFilterColumn]?.value || "")}
                    style={{
                      width: "100%",
                      padding: "6px",
                      fontSize: "12px",
                      borderRadius: "4px",
                      border: "1px solid #ddd"
                    }}
                  >
                    <option value=">">Greater than</option>
                    <option value="<">Less than</option>
                    <option value=">=">Greater than or equal</option>
                    <option value="<=">Less than or equal</option>
                    <option value="=">Equals</option>
                    <option value="!=">Does not equal</option>
                  </select>

                  <input
                    type="number"
                    placeholder="Value"
                    value={columnFilters[activeFilterColumn]?.value || ""}
                    onChange={(e) => handleNumericFilter(activeFilterColumn, columnFilters[activeFilterColumn]?.operator || ">", e.target.value)}
                    style={{
                      width: "100%",
                      padding: "6px",
                      fontSize: "12px",
                      borderRadius: "4px",
                      border: "1px solid #ddd"
                    }}
                  />
                </>
              ) : isBooleanColumn(activeFilterColumn) ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {[
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                    { label: "Blank", value: "empty" }
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "6px 8px",
                        cursor: "pointer",
                        fontSize: "12px",
                        borderRadius: "3px",
                        backgroundColor: columnFilters[activeFilterColumn]?.value === opt.value ? "#e8f4f8" : "transparent"
                      }}
                    >
                      <input
                        type="radio"
                        name="bool-filter"
                        checked={columnFilters[activeFilterColumn]?.value === opt.value}
                        onChange={() => handleNumericFilter(activeFilterColumn, "=", opt.value)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              ) : isOwnerColumn(activeFilterColumn) ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <input
                    type="text"
                    placeholder="Search values..."
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "6px",
                      fontSize: "12px",
                      borderRadius: "4px",
                      border: "1px solid #ddd",
                      marginBottom: "4px"
                    }}
                  />
                  <div style={{ display: "flex", gap: "10px", marginBottom: "4px" }}>
                    <button
                      onClick={() => {
                        const allVals = Object.keys(uniqueOwnerValues[normalizeColumnName(activeFilterColumn)] || {});
                        setColumnFilters(prev => ({
                          ...prev,
                          [activeFilterColumn]: { selectedValues: new Set(allVals) }
                        }));
                      }}
                      style={{ fontSize: "10px", color: "#3498db", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => {
                        setColumnFilters(prev => ({
                          ...prev,
                          [activeFilterColumn]: { selectedValues: new Set() }
                        }));
                      }}
                      style={{ fontSize: "10px", color: "#e74c3c", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                    >
                      Clear All
                    </button>
                  </div>
                  <div style={{
                    maxHeight: "180px",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                    border: "1px solid #eee",
                    borderRadius: "4px",
                    padding: "4px"
                  }}>
                    {Object.entries(uniqueOwnerValues[normalizeColumnName(activeFilterColumn)] || {})
                      .filter(([val]) => val.toLowerCase().includes(filterSearch.toLowerCase()))
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([val, count]) => (
                        <label
                          key={val}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "4px 6px",
                            cursor: "pointer",
                            fontSize: "12px",
                            borderRadius: "4px",
                            backgroundColor: columnFilters[activeFilterColumn]?.selectedValues?.has(val) ? "#f1faff" : "transparent"
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={columnFilters[activeFilterColumn]?.selectedValues?.has(val)}
                            onChange={() => handleOwnerFilter(activeFilterColumn, val)}
                          />
                          <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{val}</span>
                          <span style={{ fontSize: "10px", color: "#95a5a6" }}>({count})</span>
                        </label>
                      ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "4px" }}>
              <button
                onClick={() => clearColumnFilter(activeFilterColumn)}
                style={{
                  padding: "4px 10px",
                  fontSize: "11px",
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #ddd",
                  borderRadius: "3px",
                  cursor: "pointer"
                }}
              >
                Reset
              </button>
              <button
                onClick={() => setActiveFilterColumn(null)}
                style={{
                  padding: "4px 10px",
                  fontSize: "11px",
                  backgroundColor: "#34495e",
                  color: "white",
                  border: "none",
                  borderRadius: "3px",
                  cursor: "pointer"
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )
      }

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div >
  );
}

